/**
 * The atomic grammar primitives. Each renders one part of the data-state
 * grammar as text/shape/position — never colour (colour carries equipment
 * health only, UX-003 §3.7).
 */
import { useId, type ReactNode, type CSSProperties } from "react";
import type { Figure, Freshness, Provenance } from "@solarkey/api-client";
import { useLocale, formatNumber } from "../format";
import { stateWord, reasonText, formatAge } from "../grammar";

type FigureState = Figure["state"];
type Reason = NonNullable<Figure["reason"]>;

/* ── StateWord ──────────────────────────────────────────────────────────────
   The withheld-figure treatment: the plain state word in place of a number,
   with the reason one action away. Never a zero, never a bare dash. */
export function StateWord(props: {
  state: FigureState;
  reason?: Reason | null;
  className?: string;
  /** Optional inline style, e.g. to CONTAIN the word where a tile's default
   *  `.sk-tile .sk-stateword` size would make a withheld state read as a hero
   *  value (UX-REV-002 M4). Inline wins over the tile selector without a new
   *  CSS rule. */
  style?: CSSProperties;
}): ReactNode {
  const text = reasonText(props.reason);
  return (
    <span
      className={`sk-stateword${props.className ? " " + props.className : ""}`}
      data-has-reason={text ? "true" : undefined}
      title={text ?? undefined}
      style={props.style}
    >
      {stateWord(props.state)}
    </span>
  );
}

/* ── ProvenanceChip ─────────────────────────────────────────────────────────
   Where a figure came from, as a text chip (UX-003 §3.4). */
const PROVENANCE_LABEL: Record<string, string> = {
  measured: "Measured",
  reported: "Reported",
  derived: "Derived",
  modelled: "Modelled",
  forecast: "Forecast",
  declared: "Declared",
  verdict: "Verdict",
};

export function ProvenanceChip(props: {
  provenance?: Provenance | null;
}): ReactNode {
  if (!props.provenance) return null;
  return (
    <span className="sk-chip sk-chip--provenance">
      {PROVENANCE_LABEL[props.provenance] ?? props.provenance}
    </span>
  );
}

/* ── FreshnessMark ──────────────────────────────────────────────────────────
   Per-hop freshness as text (UX-003 §3.3). live shows no mark; stale shows the
   age; path_down states it. The absolute last-packet time is on demand (title). */
export function FreshnessMark(props: { freshness?: Freshness | null }): ReactNode {
  const f = props.freshness;
  if (!f) return null;

  const title = f.last_at ? `Last packet ${f.last_at}` : undefined;

  // A live figure SAYS so. It used to render nothing at all, which left a reader
  // unable to tell a current number from one that had quietly stopped moving —
  // the absence of a mark read the same either way.
  if (f.state === "live") {
    return (
      <span className="sk-freshness" data-state="live" title={title}>
        Live
      </span>
    );
  }

  if (f.state === "path_down") {
    return (
      <span className="sk-freshness" data-state="path_down" title={title}>
        Data path down{f.hop ? ` · ${f.hop}` : ""}
      </span>
    );
  }

  // Not live any more: name the instant the figure is FOR. "Stale, 9h 24m" made the
  // reader do arithmetic to find out what they actually wanted to know — which
  // reading they are looking at — and hid the answer in a tooltip.
  const at = f.last_at ? clockTime(f.last_at) : null;
  const age = typeof f.age_s === "number" ? formatAge(f.age_s) : null;
  return (
    <span className="sk-freshness" data-state="stale" title={title}>
      {at ? `as of ${at}` : `Stale${age ? `, ${age}` : ""}`}
    </span>
  );
}

/** An instant as a clock time — "10:05" — in the reader's own zone, carrying the
 *  DATE as well whenever it is not today: "23 Oct 18:10".
 *
 *  The date is not decoration. A figure may now be stamped with an instant days
 *  old (a plant dark since Friday still reports what it last held, rather than
 *  withholding it), and a bare "18:10" beside a number reads as this evening or
 *  this morning. The large type is the value and the small type is the caveat, so
 *  the caveat has to be unambiguous on its own: two days of staleness must not be
 *  able to look like twenty minutes.
 *
 *  24-hour and zero-padded so a column of same-day times lines up. Falls back to
 *  null on anything unparseable rather than printing "Invalid Date" on the glass. */
function clockTime(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return time;
  const day = d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    // A reading from last year must not read as one from last week.
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
  return `${day} ${time}`;
}

/* ── CoverageBar ────────────────────────────────────────────────────────────
   Signal-level coverage (UX-003 §3.2). Below the floor is stated in text, not
   by colour; the fill uses the neutral token. */
export function CoverageBar(props: {
  pct: number;
  floor?: number;
  className?: string;
}): ReactNode {
  const pct = clamp(props.pct, 0, 100);
  const belowFloor = typeof props.floor === "number" && pct < props.floor;
  return (
    <span
      className={`sk-coverage${props.className ? " " + props.className : ""}`}
      role="meter"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Coverage ${pct}%${belowFloor ? ", below floor" : ""}`}
    >
      <span className="sk-coverage__track">
        <span className="sk-coverage__fill" style={{ width: `${pct}%` }} />
      </span>
      <span className="sk-coverage__label">
        {pct}%{belowFloor ? " · below floor" : ""}
      </span>
    </span>
  );
}

/* ── DeltaBadge ─────────────────────────────────────────────────────────────
   A change, as a signed value with a direction shape. Informational — neutral,
   not a status colour. */
export function DeltaBadge(props: {
  value: number;
  unit?: string;
  maximumFractionDigits?: number;
}): ReactNode {
  const locale = useLocale();
  const dir = props.value > 0 ? "up" : props.value < 0 ? "down" : "flat";
  const glyph = dir === "up" ? "▲" : dir === "down" ? "▼" : "→";
  const text = formatNumber(Math.abs(props.value), locale, {
    maximumFractionDigits: props.maximumFractionDigits ?? 1,
  });
  return (
    <span className="sk-delta" data-dir={dir}>
      <span aria-hidden="true" className="sk-delta__glyph">
        {glyph}
      </span>
      {text}
      {props.unit ? <span className="sk-delta__unit"> {props.unit}</span> : null}
    </span>
  );
}

/* ── Sparkline ──────────────────────────────────────────────────────────────
   A tiny inline series, neutral stroke, no axes. Has a text summary for
   accessibility (UX-003 §8). */
export function Sparkline(props: {
  points: number[];
  width?: number;
  height?: number;
  label?: string;
}): ReactNode {
  const width = props.width ?? 72;
  const height = props.height ?? 20;
  const pts = props.points;
  if (pts.length < 2) return null;

  let min = Infinity;
  let max = -Infinity;
  for (const v of pts) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const span = max - min || 1;
  const stepX = width / (pts.length - 1);
  const d = pts
    .map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / span) * height;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const last = pts[pts.length - 1];
  const summary =
    props.label ??
    `Trend, ${pts.length} points, latest ${last ?? ""}, range ${min}–${max}`;
  return (
    <svg
      className="sk-sparkline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={summary}
      preserveAspectRatio="none"
    >
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/* ── VariantChips ───────────────────────────────────────────────────────────
   One variant shown, the others one action away; the selected variant's name
   is always visible (UX-003 §3.9 — a PR with no variant name does not exist). */
export interface VariantOption {
  key: string;
  label: string;
}

export function VariantChips(props: {
  variants: VariantOption[];
  selected: string;
  onSelect: (key: string) => void;
  ariaLabel?: string;
}): ReactNode {
  const groupId = useId();
  return (
    <span
      className="sk-variantchips"
      role="group"
      aria-label={props.ariaLabel ?? "Variant"}
    >
      {props.variants.map((v) => {
        const isSel = v.key === props.selected;
        return (
          <button
            key={v.key}
            type="button"
            id={`${groupId}-${v.key}`}
            className="sk-variantchip"
            aria-pressed={isSel}
            onClick={() => props.onSelect(v.key)}
          >
            {v.label}
          </button>
        );
      })}
    </span>
  );
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
